import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import HuddleCard from "../components/HuddleCard";
import { MODULE_DEFS } from "../lib/modules";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { honorific } from "../lib/aria";
import { readTopicFromSearch, readPersistedTopic, clearPersistedTopic, topicLabel } from "../lib/qaTopic";
import { ArrowUpRight, Sparkles, AlertTriangle, Clock, Camera, Network, Zap, X } from "lucide-react";

// Preview feature flag — Quick Assist Dashboard tile is hidden until the
// /api/ask backend workflow is functional. Set REACT_APP_QUICK_ASSIST_TILE=1
// in frontend/.env to reveal.
const QUICK_ASSIST_TILE_ENABLED =
  (process.env.REACT_APP_QUICK_ASSIST_TILE || "").trim() === "1";

export default function Dashboard() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const patientId = params.get("patient_id");
  const query = patientId ? `?patient_id=${patientId}` : "";
  const [reminders, setReminders] = useState({ deferred: [], stale_urgent: [] });

  // Pre-selected Quick-Assessment topic surviving auth (from Landing pill row).
  // URL param wins; otherwise fall back to localStorage. Cleared on dismiss.
  const initialTopic = useMemo(
    () => readTopicFromSearch(params.toString()) || readPersistedTopic(),
    [params]
  );
  const [preselectedTopic, setPreselectedTopic] = useState(initialTopic);
  const preselectedTopicLabel = preselectedTopic ? topicLabel(preselectedTopic) : null;

  const dismissTopic = () => {
    clearPersistedTopic();
    setPreselectedTopic(null);
  };

  useEffect(() => {
    api.get("/case-analysis/reminders").then((r) => setReminders(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="kicker">Home</div>
      <h1 className="font-serif text-3xl md:text-4xl mt-2 text-[var(--pa-text)]" data-testid="dashboard-title">
        Welcome, {honorific(user?.full_name)}.
      </h1>
      <p className="mt-3 text-[var(--pa-text-muted)] max-w-2xl">
        {patientId
          ? "Select a clinical module to start a consultation for this patient."
          : "Start with a Complete Case Analysis, or jump straight into a clinical module."}
      </p>

      {preselectedTopicLabel && (
        <div
          data-testid="dashboard-preselected-topic"
          className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-[var(--pa-gold)] bg-[var(--pa-gold)]/10 px-4 py-3"
        >
          <span className="kicker text-[var(--pa-gold)]">Continuing with</span>
          <span
            data-testid="dashboard-preselected-topic-label"
            className="text-[var(--pa-text)] font-semibold text-sm"
          >
            {preselectedTopicLabel}
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Link
              to={`/quick-assessment/new?topic=${preselectedTopic}`}
              onClick={() => clearPersistedTopic()}
              data-testid="dashboard-preselected-topic-continue"
              className="inline-flex items-center gap-1.5 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition text-sm"
            >
              Continue <ArrowUpRight size={14} />
            </Link>
            <button
              type="button"
              onClick={dismissTopic}
              data-testid="dashboard-preselected-topic-dismiss"
              aria-label="Dismiss pre-selected topic"
              className="p-2 text-[var(--pa-text-muted)] hover:text-[var(--pa-text)] transition"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <HuddleCard />

      {(reminders.stale_urgent.length > 0 || reminders.deferred.length > 0) && (
        <div className="mt-8 space-y-3" data-testid="dashboard-reminders">
          {reminders.stale_urgent.map((r, i) => (
            <Link
              key={`stale-${i}`}
              to={`/case-analysis/${r.analysis_id}`}
              data-testid={`stale-urgent-${r.analysis_id}`}
              className="block bg-red-500/10 border border-red-500/40 rounded-lg p-4 hover:bg-red-500/15 transition"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
                <div>
                  <div className="kicker text-red-400">90-day urgent finding alert</div>
                  <div className="text-sm text-[var(--pa-text)] mt-1">
                    A previously identified urgent finding for patient{" "}
                    <strong>{r.patient_initials || "—"}</strong> has been unaddressed for 90
                    days. Please review and update clinical status.
                  </div>
                  <div className="text-xs text-[var(--pa-text-muted)] mt-1">{r.finding}</div>
                </div>
              </div>
            </Link>
          ))}
          {reminders.deferred.map((r) => (
            <Link
              key={r.analysis_id}
              to={`/case-analysis/${r.analysis_id}`}
              data-testid={`deferred-${r.analysis_id}`}
              className="block bg-[var(--pa-surface)] border border-amber-400/40 rounded-lg p-4 hover:border-amber-400 transition"
            >
              <div className="flex items-start gap-3">
                <Clock size={16} className="text-amber-300 mt-0.5 shrink-0" />
                <div className="text-sm text-[var(--pa-text)]">
                  Previous Full Mouth Analysis dated{" "}
                  {r.acknowledged_at ? new Date(r.acknowledged_at).toLocaleDateString() : "—"}{" "}
                  identified <strong>{r.deferred_count}</strong> deferred finding
                  {r.deferred_count === 1 ? "" : "s"} for patient{" "}
                  <strong>{r.patient_initials || "—"}</strong>. Tap to review.
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Assist tile (feature-flagged; hidden until /api/ask is functional) */}
      {QUICK_ASSIST_TILE_ENABLED && (
        <Link
          to="/quick-assist"
          data-testid="dashboard-quick-assist-tile"
          className="block mt-8 group bg-[var(--pa-surface)] border-2 border-[var(--pa-gold)] rounded-lg p-5 md:p-6 transition hover:shadow-[0_0_50px_-10px_rgba(212,175,55,0.45)]"
        >
          <div className="flex items-center gap-5">
            <div className="shrink-0 w-14 h-14 rounded-full bg-[var(--pa-gold)]/15 border border-[var(--pa-gold)] flex items-center justify-center">
              <Zap size={24} className="text-[var(--pa-gold)]" />
            </div>
            <div className="flex-1">
              <div className="kicker text-[var(--pa-gold)]">Chairside · Quick Assist</div>
              <h2 className="font-serif text-xl md:text-2xl text-[var(--pa-text)] mt-1">
                Fast clinical answers — general guidance in seconds.
              </h2>
              <p className="mt-1.5 text-xs text-[var(--pa-text-muted)] max-w-xl">
                Speak or type a general clinical question. For patient-specific reasoning, use Full AI Consultation.
              </p>
            </div>
            <ArrowUpRight
              size={18}
              className="shrink-0 text-[var(--pa-gold)] group-hover:translate-x-1 group-hover:-translate-y-0.5 transition"
            />
          </div>
        </Link>
      )}

      {/* Tier 1 — Quick Assessment (primary mobile entry) */}
      <Link
        to="/quick-assessment/new"
        data-testid="dashboard-quick-assessment-card"
        className="block mt-8 group bg-[var(--pa-surface)] border-2 border-[var(--pa-gold)] rounded-lg p-5 md:p-6 transition hover:shadow-[0_0_50px_-10px_rgba(212,175,55,0.45)]"
      >
        <div className="flex items-center gap-5">
          <div className="shrink-0 w-14 h-14 rounded-full bg-[var(--pa-gold)]/15 border border-[var(--pa-gold)] flex items-center justify-center">
            <Camera size={24} className="text-[var(--pa-gold)]" />
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">Tier 1 · Quick Assessment</div>
            <h2 className="font-serif text-xl md:text-2xl text-[var(--pa-text)] mt-1">
              Snap. Organise. Route — in 30 seconds.
            </h2>
            <p className="mt-1.5 text-xs text-[var(--pa-text-muted)] max-w-xl">
              Photograph your X-rays or patient from any screen, lightbox, or chairside.
              Prepare a structured case intake for specialist review.
            </p>
          </div>
          <ArrowUpRight
            size={18}
            className="shrink-0 text-[var(--pa-gold)] group-hover:translate-x-1 group-hover:-translate-y-0.5 transition"
          />
        </div>
      </Link>

      {/* Signature Complete Case Analysis card */}
      <Link
        to="/case-analysis/new"
        data-testid="signature-cca-card"
        className="block mt-5 group bg-gradient-to-br from-[var(--pa-surface)] to-[var(--pa-bg-2)] border-2 border-[var(--pa-gold)] rounded-lg p-6 md:p-8 transition hover:shadow-[0_0_60px_-10px_rgba(212,175,55,0.45)]"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="shrink-0">
            <div className="w-16 h-16 rounded-full bg-[var(--pa-gold)]/15 border border-[var(--pa-gold)] flex items-center justify-center">
              <Sparkles size={26} className="text-[var(--pa-gold)]" />
            </div>
          </div>
          <div className="flex-1">
            <div className="kicker text-[var(--pa-gold)]">ProsthoAI Complete Case Analysis</div>
            <h2 className="font-serif text-2xl md:text-3xl text-[var(--pa-text)] mt-1.5">
              See the whole picture before treating the part.
            </h2>
            <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
              State your concern. ProsthoAI analyzes the entire mouth — starting with
              occlusion — then answers your question in full clinical context.
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2 text-[var(--pa-gold)] font-semibold">
            Start analysis{" "}
            <ArrowUpRight
              size={18}
              className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition"
            />
          </div>
        </div>
      </Link>

      <div className="gold-divider my-10" />

      {/* Interdisciplinary Cases shortcut */}
      <Link
        to="/collab-cases"
        data-testid="dashboard-collab-card"
        className="block mb-8 p-5 rounded-lg bg-[var(--pa-surface)] border border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60 transition group"
      >
        <div className="flex items-center gap-4">
          <Network size={22} className="text-[var(--pa-gold)] shrink-0" />
          <div className="flex-1">
            <div className="kicker">Interdisciplinary Cases</div>
            <div className="text-sm text-[var(--pa-text)] mt-1">
              Invite specialists, collect their input, and synthesize a comprehensive plan.
            </div>
          </div>
          <ArrowUpRight size={16} className="text-[var(--pa-text-dim)] group-hover:text-[var(--pa-gold)]" />
        </div>
      </Link>

      <div className="kicker mb-4">Individual clinical modules</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {Object.entries(MODULE_DEFS).map(([slug, def]) => {
          const Icon = def.Icon;
          return (
            <Link
              key={slug}
              to={`/module/${slug}${query}`}
              data-testid={`module-card-${slug}`}
              className="module-card bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between">
                <Icon size={22} className="text-[var(--pa-gold)]" />
                <ArrowUpRight
                  size={16}
                  className="text-[var(--pa-text-dim)] group-hover:text-[var(--pa-gold)] transition"
                />
              </div>
              <div className="font-serif text-lg mt-5 text-[var(--pa-text)] leading-tight">
                {def.name}
              </div>
              <div className="mt-2 text-xs text-[var(--pa-text-muted)] leading-relaxed">
                {def.description}
              </div>
            </Link>
          );
        })}
      </div>
    </Layout>
  );
}
